import React, { useState } from "react";
import { Button, Col, Modal, Row, Upload, notification, Progress } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import ProductService from "services/product";
import OrderService from "services/order";
import ImportLog from "components/ImportLog/ImportLog";
import { useModalDirectLinkContext } from "components/ModalDirectLink";
import addKeyLocalStorage from "helper/localStorage";
import { number_to_price } from "helper/common";
import { normalizePlate, validatePlateNumber } from "helper/orderHelper";
import moment from "moment";
import * as XLSX from "xlsx";

const GIFT_IMPORT_SHEET_NAME = "Danh sách tặng gói";

const GIFT_IMPORT_HEADER = {
  ID: "ID",
  START_TIME: "Start time",
  COMPLETION_TIME: "Completion time",
  EMAIL: "Email",
  NAME: "Name",
  LAST_MODIFIED_TIME: "Last modified time",
  PHONE: "Số điện thoại đăng kí Zalopay của Starter (vd: 0912345678)",
  LICENSE_PLATE_1: "Biển số xe của Starter (ví dụ: 59A1-12345)",
  VEHICLE_TYPE_1: "Starter đang sử dụng loại xe nào",
  LICENSE_PLATE_2: "Biển số xe của Starter (ví dụ: 59A1-12345)2",
  VEHICLE_TYPE_2: "Starter đang sử dụng loại xe nào2",
  PRODUCT_SKU: "Tên quà tặng",
};

const GIFT_IMPORT_REQUIRED_HEADERS = [
  GIFT_IMPORT_HEADER.START_TIME,
  GIFT_IMPORT_HEADER.EMAIL,
  GIFT_IMPORT_HEADER.NAME,
  GIFT_IMPORT_HEADER.PHONE,
  GIFT_IMPORT_HEADER.LICENSE_PLATE_1,
  GIFT_IMPORT_HEADER.VEHICLE_TYPE_1,
  GIFT_IMPORT_HEADER.LICENSE_PLATE_2,
  GIFT_IMPORT_HEADER.VEHICLE_TYPE_2,
  GIFT_IMPORT_HEADER.PRODUCT_SKU,
];

const VEHICLE_TYPE_MAPPING = {
  "Xe ô tô": 1,
  "Xe gắn máy": 30,
  "Xe máy điện": 32,
};

const normalizeImportKey = (value) => String(value || "").trim().toUpperCase();

const getDisplayAppsLabel = (displayApps) => !displayApps ? "" : `${displayApps}`
  .split(",")
  .map((item) => item.trim())
  .filter(Boolean)
  .join(", ");

const getGiftProductOptionLabel = (product) => {
  const displayAppsLabel = getDisplayAppsLabel(product?.displayApps);
  const priceLabel = `${number_to_price(product?.productPrice || 0)} VNĐ`;

  return `${product?.productName || ""} | ${displayAppsLabel} | ${priceLabel}`;
};

const parseImportStartTime = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return moment(value).format("YYYY-MM-DD HH:mm:ss");
  }

  const rawValue = String(value || "").trim();

  if (!rawValue) {
    return "";
  }

  const formats = [
    "M/D/YY H:mm:ss",
    "M/D/YY H:mm",
    "M/D/YYYY H:mm:ss",
    "M/D/YYYY H:mm",
    "DD/MM/YYYY HH:mm:ss",
    "DD/MM/YYYY HH:mm",
    "DD/MM/YYYY",
    "YYYY-MM-DD HH:mm:ss",
    "YYYY-MM-DD HH:mm",
    "YYYY-MM-DD",
  ];

  const parsedDate = moment(rawValue, formats, true);

  if (!parsedDate.isValid()) {
    return "";
  }

  return parsedDate.format("YYYY-MM-DD HH:mm:ss");
};

const parseGiftImportFile = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();

  reader.onload = (evt) => {
    const workbook = XLSX.read(evt.target.result, {
      type: "binary",
      cellDates: true,
    });

    if (!workbook.SheetNames.includes(GIFT_IMPORT_SHEET_NAME)) {
      resolve({ isError: true, data: [] });
      return;
    }

    const worksheet = workbook.Sheets[GIFT_IMPORT_SHEET_NAME];

    resolve({
      isError: false,
      data: XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        raw: false,
        defval: "",
      }),
    });
  };

  reader.onerror = reject;
  reader.readAsBinaryString(file);
});

const validatePhoneNumber = (value) => {
  const phone = String(value || "").trim();
  const phonePattern = /^(03|05|07|08|09|01[2|6|8|9])+([0-9]){8}$/;
  if (!phone) {
    return "Vui lòng nhập số điện thoại";
  }
  if (phone.length < 10) {
    return "Số điện thoại quá ngắn";
  }
  if (phone.length > 11) {
    return "Số điện thoại quá dài";
  }
  if (!phonePattern.test(phone)) {
    return "Số điện thoại không hợp lệ";
  }
  return "";
};

const validateImportEmail = (value) => {
  const email = String(value || "").trim();
  const emailPattern = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
  if (!email) {
    return "Vui lòng nhập email";
  }
  if (!emailPattern.test(email)) {
    return "Email không hợp lệ";
  }
  return "";
};

const getImportRows = (rows) => {
  const headerIndex = rows.findIndex((row) => {
    const normalizedRow = row.map((cell) => String(cell || "").trim());
    return GIFT_IMPORT_REQUIRED_HEADERS.every((header) => normalizedRow.includes(header));
  });

  if (headerIndex < 0) {
    return { isError: true, data: [] };
  }

  const headers = rows[headerIndex].map((cell) => String(cell || "").trim());

  const getCellValue = (row, headerName) => {
    const index = headers.indexOf(headerName);
    return index >= 0 ? row[index] : "";
  };

  const data = rows.slice(headerIndex + 1)
    .filter((row) => row.some((cell) => String(cell || "").trim()))
    .map((row, rowIndex) => ({
      rowNumber: headerIndex + rowIndex + 2,
      startTime: getCellValue(row, GIFT_IMPORT_HEADER.START_TIME),
      email: getCellValue(row, GIFT_IMPORT_HEADER.EMAIL),
      name: getCellValue(row, GIFT_IMPORT_HEADER.NAME),
      phone: getCellValue(row, GIFT_IMPORT_HEADER.PHONE),
      licensePlate1: getCellValue(row, GIFT_IMPORT_HEADER.LICENSE_PLATE_1),
      vehicleType1: getCellValue(row, GIFT_IMPORT_HEADER.VEHICLE_TYPE_1),
      licensePlate2: getCellValue(row, GIFT_IMPORT_HEADER.LICENSE_PLATE_2),
      vehicleType2: getCellValue(row, GIFT_IMPORT_HEADER.VEHICLE_TYPE_2),
      productSKU: getCellValue(row, GIFT_IMPORT_HEADER.PRODUCT_SKU),
    }));

  return { isError: false, data };
};

const getProductMapBySKU = async (items) => {
  const productMap = {};
  const productSKUs = [...new Set(items.map((item) => String(item.productSKU || "").trim()).filter(Boolean))];
  if (productSKUs.length === 0) {
    return productMap;
  }

  const response = await ProductService.advanceUserGetList({
    filter: {
      isGiftEnabled: 1,
    },
    limit: 200,
  });

  const allGiftProducts = (response?.data || []).filter((product) => !product.productSKU?.toUpperCase().includes("UUDAI"));
  const member = JSON.parse(window.localStorage.getItem(addKeyLocalStorage("data"))) || {};
  const stationCode = member.stationCode;
  const stationsId = member.stationsId;

  productSKUs.forEach((productSKU) => {
    const upperSKU = productSKU.toUpperCase();
    const prefixedSKU = stationCode ? `${stationCode}_${productSKU}`.toUpperCase() : "";

    const matchedProducts = allGiftProducts.filter((product) => {
      const dbSKU = normalizeImportKey(product.productSKU);
      const dbName = normalizeImportKey(product.productName);
      const dbWebLabel = normalizeImportKey(getGiftProductOptionLabel(product));

      return (
        dbSKU === upperSKU ||
        dbName === upperSKU ||
        dbWebLabel === upperSKU ||
        (prefixedSKU && dbSKU === prefixedSKU)
      );
    });
    if (matchedProducts.length > 0) {
      const stationProduct = matchedProducts.find((p) => p.stationsId === stationsId);
      const matchedProduct = stationProduct || matchedProducts[0];
      productMap[productSKU] = matchedProduct.productId;
    }
  });

  return productMap;
};

const validateImportItem = (item) => {
  const phoneError = validatePhoneNumber(item.phone);
  if (phoneError) {
    return phoneError;
  }

  const emailError = validateImportEmail(item.email);
  if (emailError) {
    return emailError;
  }

  const notifyStartDate = parseImportStartTime(item.startTime);
  if (!notifyStartDate) {
    return "Start time không được để trống hoặc sai định dạng";
  }
  item.notifyStartDate = notifyStartDate;

  if (!item.productId) {
    return `Không tìm thấy tên quà tặng: ${item.productSKU || ""}`;
  }

  const vehicles = [];

  if (item.licensePlate1 || item.vehicleType1) {
    if (!item.licensePlate1) {
      return "Vui lòng nhập biển số xe 1";
    }

    if (!item.vehicleType1) {
      return "Vui lòng nhập loại xe 1";
    }

    if (!VEHICLE_TYPE_MAPPING[item.vehicleType1]) {
      return `Loại xe không hợp lệ: ${item.vehicleType1}`;
    }

    const rawPlate1 = String(item.licensePlate1 || "");
    const plateError = validatePlateNumber(rawPlate1);

    if (plateError) {
      return plateError;
    }

    const normalizedPlate1 = normalizePlate(rawPlate1);
    vehicles.push({ licensePlate: normalizedPlate1, vehicleType: item.vehicleType1 });
  }

  if (item.licensePlate2 || item.vehicleType2) {
    if (!item.licensePlate2) {
      return "Vui lòng nhập biển số xe 2";
    }

    if (!item.vehicleType2) {
      return "Vui lòng nhập loại xe 2";
    }

    if (!VEHICLE_TYPE_MAPPING[item.vehicleType2]) {
      return `Loại xe không hợp lệ: ${item.vehicleType2}`;
    }

    const rawPlate2 = String(item.licensePlate2 || "");
    const plateError = validatePlateNumber(rawPlate2);

    if (plateError) {
      return plateError;
    }

    const normalizedPlate2 = normalizePlate(rawPlate2);
    vehicles.push({ licensePlate: normalizedPlate2, vehicleType: item.vehicleType2 });
  }

  if (!vehicles.length) {
    return "Vui lòng nhập ít nhất một biển số và loại xe";
  }

  item.vehicles = vehicles;
  return "";
};

const prepareImportItems = async (items) => {
  const productMap = await getProductMapBySKU(items);
  const finalItems = [];
  const seen = new Set();

  items.forEach((item) => {
    item.productId = productMap[String(item.productSKU || "").trim()];
    const errorMessage = validateImportItem(item);
    if (errorMessage) {
      finalItems.push({ ...item, errorMessage });
      return;
    }

    const vehiclesByType = item.vehicles.reduce((result, vehicle) => {
      result[vehicle.vehicleType] = result[vehicle.vehicleType] || [];
      result[vehicle.vehicleType].push(vehicle);
      return result;
    }, {});

    Object.keys(vehiclesByType).forEach((vehicleType) => {
      const key = [
        String(item.phone || "").trim(),
        String(item.name || "").trim(),
        item.productId,
        vehiclesByType[vehicleType].map((vehicle) => vehicle.licensePlate).join("_"),
      ].join("_");
      if (!seen.has(key)) {
        seen.add(key);
        finalItems.push({ ...item, vehicles: vehiclesByType[vehicleType] });
      }
    });
  });

  return finalItems;
};

const GiftOrderImportModal = ({
  visible,
  onCancel,
  onSuccess,
  giftProducts = [],
  giftProductProvider = "ALL",
}) => {
  const { setUrlForModalDirectLink } = useModalDirectLinkContext();
  const [importPercent, setImportPercent] = useState(0);
  const [importLogs, setImportLogs] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [currentProcessingIndex, setCurrentProcessingIndex] = useState(0);

const handleDownloadSampleFile = () => {
  const GIFT_SAMPLE_PROVIDERS = ["TAMOVE", "TTDK"];

  const packageOptions = (giftProducts || [])
    .filter((product) => {
      const provider = String(product.productProvider || "").toUpperCase();
      return GIFT_SAMPLE_PROVIDERS.includes(provider);
    })
    .filter((product) => !product.productSKU?.toUpperCase().includes("UUDAI"))
    .map((product) => ({
      sku: product.productSKU || "",
      name: product.productName || "",
      price: product.productPrice || 0,
      displayApps: product.displayApps || "",
      provider: product.productProvider || "",
    }))
    .filter((product) => product.sku || product.name);

  const getPackageSKUByIndex = (index) => {
    if (!packageOptions.length) return "";
    return packageOptions[index % packageOptions.length]?.sku || "";
  };

  const importHeaders = [
    GIFT_IMPORT_HEADER.ID,
    GIFT_IMPORT_HEADER.START_TIME,
    GIFT_IMPORT_HEADER.COMPLETION_TIME,
    GIFT_IMPORT_HEADER.EMAIL,
    GIFT_IMPORT_HEADER.NAME,
    GIFT_IMPORT_HEADER.LAST_MODIFIED_TIME,
    GIFT_IMPORT_HEADER.PHONE,
    GIFT_IMPORT_HEADER.LICENSE_PLATE_1,
    GIFT_IMPORT_HEADER.VEHICLE_TYPE_1,
    GIFT_IMPORT_HEADER.LICENSE_PLATE_2,
    GIFT_IMPORT_HEADER.VEHICLE_TYPE_2,
    GIFT_IMPORT_HEADER.PRODUCT_SKU,
  ];

 const importRows = [
  [
    1,
    "1/21/26 10:36:26",
    "1/21/26 10:36:42",
    "example@gmail.com",
    "Đàm Văn A",
    "",
    "0359494550",
    "67N168350",
    "Xe gắn máy",
    "",
    "",
    getPackageSKUByIndex(0),
  ],
  [
    2,
    "1/21/26 10:39:13",
    "1/21/26 10:39:41",
    "example@gmail.com",
    "Phan Thị D",
    "",
    "0965548668",
    "51K71619",
    "Xe ô tô",
    "",
    "",
    getPackageSKUByIndex(1),
  ],
  [
    3,
    "1/21/26 10:42:27",
    "1/21/26 10:44:11",
    "example@gmail.com",
    "Hoàng Văn C",
    "",
    "0927134845",
    "59N167373",
    "Xe gắn máy",
    "",
    "",
    getPackageSKUByIndex(2),
  ],
  [
    4,
    "1/21/26 10:40:14",
    "1/21/26 10:46:41",
    "example@gmail.com",
    "Lê Thị B",
    "",
    "0961242418",
    "50AC19543",
    "Xe máy điện",
    "",
    "",
    getPackageSKUByIndex(3),
  ],
  [
    5,
    "1/21/26 10:48:39",
    "1/21/26 10:50:05",
    "example@gmail.com",
    "Nguyễn Văn E",
    "",
    "0908081000",
    "51L37842",
    "Xe ô tô",
    "59V308650",
    "Xe gắn máy",
    getPackageSKUByIndex(4),
  ],
];
  const importSheet = XLSX.utils.aoa_to_sheet([
    importHeaders,
    ...importRows,
  ]);

  importSheet["!cols"] = [
    { wch: 8 },
    { wch: 20 },
    { wch: 22 },
    { wch: 28 },
    { wch: 24 },
    { wch: 22 },
    { wch: 48 },
    { wch: 40 },
    { wch: 32 },
    { wch: 40 },
    { wch: 32 },
    { wch: 22 },
  ];

  const packageSheet = XLSX.utils.aoa_to_sheet([
    ["SKU", "Tên quà tặng", "Ứng dụng", "Giá"],
    ...packageOptions.map((product) => [
      product.sku,
      product.name,
      product.displayApps,
      product.price,
    ]),
  ]);

  packageSheet["!cols"] = [
    { wch: 24 },
    { wch: 42 },
    { wch: 24 },
    { wch: 16 },
  ];

  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(
    workbook,
    importSheet,
    GIFT_IMPORT_SHEET_NAME
  );

  XLSX.utils.book_append_sheet(
    workbook,
    packageSheet,
    "Danh sách gói"
  );

  XLSX.writeFile(workbook, "giftNotiSample.xlsx");
};

  const handleImportGiftFile = async (file) => {
    if (!/\.xlsx$/i.test(file.name || "")) {
      notification.error({ message: "Chỉ hỗ trợ định dạng .xlsx" });
      return;
    }

    setIsImporting(true);
    setImportPercent(0);
    setImportLogs([]);
    setCurrentProcessingIndex(0);

    try {
      const parsedFile = await parseGiftImportFile(file);
      if (parsedFile.isError) {
        notification.error({ message: "File nhập không đúng định dạng" });
        return;
      }
      const parsedRows = getImportRows(parsedFile.data);
      if (parsedRows.isError) {
        notification.error({ message: "File thiếu cột bắt buộc" });
        return;
      }

      const importItems = await prepareImportItems(parsedRows.data);
      if (!importItems.length) {
        notification.error({ message: "File không có dữ liệu để nhập" });
        return;
      }
      let successCount = 0;
      let errorCount = 0;
      const logs = [];

      for (let index = 0; index < importItems.length; index += 1) {
        const item = importItems[index];
        const rowNumber = item.rowNumber || index + 1;
        if (item.errorMessage) {
          errorCount += 1;
          logs.push({ message: `Dòng ${rowNumber}: ${item.errorMessage}`, status: 'error', id: index });
        } else {
          for (const vehicle of item.vehicles) {
            const result = await OrderService.createGiftOrder({
              requestId: `${String(item.phone || "").trim()}_${Date.now()}_${Math.random()}`,
              phoneNumber: String(item.phone || "").trim(),
              email: String(item.email || "").trim(),
              firstName: String(item.name || ""),
              licensePlatesList: [vehicle.licensePlate],
              vehicleType: VEHICLE_TYPE_MAPPING[vehicle.vehicleType],
              productIds: [item.productId],
              totalPayment: null,
              notifyStartDate: item.notifyStartDate,
            });

            if (result?.statusCode === 200) {
              successCount += 1;
              logs.push({ message: `Dòng ${rowNumber} - ${vehicle.licensePlate}: Thành công`, status: 'success', id: `${index}_${vehicle.licensePlate}` });
            } else {
              errorCount += 1;
              const errorMessage = {
                UNCONFIRMED_BOOKING_EXISTED: 'Xe đã có gói tương tự đang hoạt động',
              }[result?.error] || (result?.error || "Tặng thất bại");
              logs.push({ message: `Dòng ${rowNumber} - ${vehicle.licensePlate}: ${errorMessage}`, status: 'error', id: `${index}_${vehicle.licensePlate}` });
            }
          }
        }

        setImportLogs([...logs]);
        setCurrentProcessingIndex(index);
        setImportPercent(((index + 1) / importItems.length) * 100);
      }

      if (successCount > 0) {
        onSuccess();
      }

      // Notify completion
      if (errorCount === 0) {
        notification.success({ message: `Import thành công ${successCount} bản ghi` });
      } else if (successCount === 0) {
        notification.error({ message: `Import thất bại toàn bộ ${errorCount} bản ghi` });
      } else {
        notification.warning({ message: `Import hoàn tất: ${successCount} thành công, ${errorCount} thất bại` });
      }
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <>
      <Modal
        title="Nhập file quà tặng"
        visible={visible}
        onCancel={onCancel}
        footer={null}
        destroyOnClose
      >
        {importLogs.length === 0 && !isImporting ? (
          <Row gutter={16}>
            <Col span={12}>
              <p>Chọn file excel</p>
              <Upload
                accept=".xlsx"
                showUploadList={false}
                beforeUpload={(file) => {
                  handleImportGiftFile(file);
                  return false;
                }}
              >
                <Button icon={<UploadOutlined />}>
                  Tải file lên
                </Button>
              </Upload>
            </Col>
            <Col span={12}>
              <p>Tham khảo file mẫu</p>
           <Button
            type="primary"
            onClick={handleDownloadSampleFile}
          >
            Tải file mẫu
          </Button>
            </Col>
          </Row>
        ) : (
          <div style={{ width: "100%" }}>
            <div className="mb-2">
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span>Tiến độ: {importLogs.length} / {importLogs.length + (isImporting ? 1 : 0)}</span>
                <span>{Math.round(importPercent)}%</span>
              </div>
              <Progress percent={Math.round(importPercent)} size="default" />
            </div>

            <div style={{ maxHeight: "400px", overflowY: "auto" }}>
              <ImportLog
                logs={importLogs}
                isProcessing={isImporting}
                currentIndex={currentProcessingIndex}
              />
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default GiftOrderImportModal;
